import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, Button, Text, Badge, tokens } from '../../design-system';
import { useUpdateDiscoveryQuestions } from '../../hooks/api/useIdeas';
import { useAnalyzeIdeaEdit, useApplyIdeaEdit } from '../../hooks/api/useIdeaEdit';
import IdeaCascadeModal from '../IdeaCascadeModal';
import { v4 as uuidv4 } from 'uuid';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  mossSoft:  '#E8EDDB',
  mossDark:  '#4E5A2A',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Assumption category configuration (matches engineering decisions style)
export const ASSUMPTION_CATEGORIES = {
  users:       { label: 'Users & Audience',      glyph: '◯', color: T.orange },
  problem:     { label: 'Problem & Value',        glyph: '✦', color: T.capRed },
  features:    { label: 'Core Features',          glyph: '◇', color: T.moss },
  constraints: { label: 'Constraints & Context', glyph: '⌗', color: T.inkMuted },
};

// Assumptions Content Component - matches DecisionsTab styling
const AssumptionsContent = ({ idea, onRefetch }) => {
  const [localAssumptions, setLocalAssumptions] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addingToCategory, setAddingToCategory] = useState(null);
  const [newAssumptionText, setNewAssumptionText] = useState('');
  const [cascadeModal, setCascadeModal] = useState({ open: false, data: null, pendingSave: null });

  const updateQuestionsMutation = useUpdateDiscoveryQuestions();
  const analyzeMutation = useAnalyzeIdeaEdit();
  const applyMutation = useApplyIdeaEdit();

  // Use local assumptions if available, otherwise fall back to server data.
  // idea.discovery_questions is kept as a read fallback for ideas created before the current flow.
  const serverAssumptions = useMemo(
    () => idea.assumptions || idea.discovery_questions || [],
    [idea.assumptions, idea.discovery_questions]
  );
  const assumptions = localAssumptions || serverAssumptions;
  const hasAssumptions = assumptions.length > 0;

  // Sync local assumptions from server data (including after undo/rollback)
  const serverQuestionsRef = React.useRef(serverAssumptions);
  useEffect(() => {
    if (serverAssumptions.length > 0 && (
      localAssumptions === null ||
      serverAssumptions !== serverQuestionsRef.current
    )) {
      setLocalAssumptions(serverAssumptions);
      serverQuestionsRef.current = serverAssumptions;
    }
  }, [serverAssumptions, localAssumptions]);

  // Build values from saved answers + suggested answers (AI assumptions)
  const values = useMemo(() => {
    const result = { ...(idea.discovery_answers || {}) };
    assumptions.forEach(a => {
      if (!result[a.id] && (a.suggested_answer || a.suggested_value)) {
        result[a.id] = a.suggested_answer || a.suggested_value;
      }
    });
    return result;
  }, [idea.discovery_answers, assumptions]);

  // Check if a value is an AI assumption (matches the suggested_answer)
  const isAIAssumption = (assumptionId) => {
    const assumption = assumptions.find(a => a.id === assumptionId);
    const suggestedAnswer = assumption?.suggested_answer || assumption?.suggested_value;
    if (!suggestedAnswer) return false;
    const value = values[assumptionId];
    const suggested = suggestedAnswer;
    // Handle array comparison
    if (Array.isArray(value) && Array.isArray(suggested)) {
      return JSON.stringify([...value].sort()) === JSON.stringify([...suggested].sort());
    }
    return value === suggested;
  };

  // Group assumptions by category
  const assumptionsByCategory = useMemo(() => {
    return assumptions.reduce((acc, a) => {
      const category = a.category || 'general';
      if (!acc[category]) acc[category] = [];
      acc[category].push({ ...a, value: values[a.id] });
      return acc;
    }, {});
  }, [assumptions, values]);

  // Stats
  const totalAssumptions = assumptions.length;
  const confirmedAssumptions = Object.keys(values).filter(k => {
    const val = values[k];
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'string') return val.trim().length > 0;
    return !!val;
  }).length;

  // Add a new assumption
  const handleAddAssumption = useCallback((category) => {
    if (!newAssumptionText.trim()) return;

    const newAssumption = {
      id: `custom_${uuidv4().slice(0, 8)}`,
      category: category,
      question: newAssumptionText.trim(),
      type: 'text',
      importance: 'optional',
    };

    const updatedAssumptions = [...(localAssumptions || []), newAssumption];
    setLocalAssumptions(updatedAssumptions);
    updateQuestionsMutation.mutate({ ideaId: idea.id, questions: updatedAssumptions });
    setNewAssumptionText('');
    setAddingToCategory(null);
  }, [newAssumptionText, localAssumptions, updateQuestionsMutation, idea.id]);

  // Remove an assumption
  const handleRemoveAssumption = useCallback((assumptionId) => {
    const updatedAssumptions = (localAssumptions || []).filter(a => a.id !== assumptionId);
    setLocalAssumptions(updatedAssumptions);
    updateQuestionsMutation.mutate({ ideaId: idea.id, questions: updatedAssumptions });
  }, [localAssumptions, updateQuestionsMutation, idea.id]);

  // Handle edit
  const handleEdit = (assumption) => {
    setEditingQuestion(assumption.id);
    setEditValue(values[assumption.id] || '');
  };

  // Handle save - analyze cascade effects before persisting
  const handleSave = useCallback(async () => {
    if (!editingQuestion || !localAssumptions) return;

    const oldAnswers = idea.discovery_answers || {};
    const newAnswers = { ...oldAnswers, [editingQuestion]: editValue };

    // Try cascade analysis
    try {
      const result = await analyzeMutation.mutateAsync({
        ideaId: idea.id,
        section: 'discovery_answers',
        changes: { value: newAnswers },
      });

      if (result.cascade_changes && result.cascade_changes.length > 0) {
        // Store pending save and show cascade modal
        setCascadeModal({
          open: true,
          data: result,
          pendingSave: { editingQuestion, editValue, updatedAssumptions: localAssumptions.map(a =>
            a.id === editingQuestion ? { ...a, suggested_answer: editValue } : a
          ), newAnswers },
        });
        return;
      }
    } catch (err) {
      console.error('Cascade analysis failed, saving directly:', err);
    }

    // No cascades or analysis failed - apply through edit system for history tracking
    const updatedAssumptions = localAssumptions.map(a =>
      a.id === editingQuestion ? { ...a, suggested_answer: editValue } : a
    );
    try {
      await applyMutation.mutateAsync({
        ideaId: idea.id,
        section: 'discovery_answers',
        changes: { value: newAnswers },
        approvedCascades: [],
      });
      onRefetch?.();
    } catch (err) {
      console.error('Apply failed, saving directly:', err);
      updateQuestionsMutation.mutate({ ideaId: idea.id, questions: updatedAssumptions });
    }
    setLocalAssumptions(updatedAssumptions);
    setEditingQuestion(null);
    setEditValue('');
  }, [editingQuestion, editValue, localAssumptions, updateQuestionsMutation, analyzeMutation, applyMutation, idea.id, idea.discovery_answers, onRefetch]);

  // Handle cascade modal apply
  const handleCascadeApply = useCallback(async (approvedCascades) => {
    const { pendingSave } = cascadeModal;
    if (!pendingSave) return;

    try {
      await applyMutation.mutateAsync({
        ideaId: idea.id,
        section: 'discovery_answers',
        changes: { value: pendingSave.newAnswers },
        approvedCascades: approvedCascades,
      });
      onRefetch?.();
    } catch (err) {
      console.error('Failed to apply cascades:', err);
      updateQuestionsMutation.mutate({ ideaId: idea.id, questions: pendingSave.updatedAssumptions });
    }
    setLocalAssumptions(pendingSave.updatedAssumptions);

    setCascadeModal({ open: false, data: null, pendingSave: null });
    setEditingQuestion(null);
    setEditValue('');
    onRefetch?.();
  }, [cascadeModal, applyMutation, updateQuestionsMutation, idea.id, onRefetch]);

  // Handle cancel
  const handleCancel = () => {
    setEditingQuestion(null);
    setEditValue('');
  };

  if (!hasAssumptions) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>💡</span>
        <span style={{ color: T.inkMuted, fontFamily: T.sans, fontSize: 14 }}>
          Assumptions are being generated...
        </span>
      </div>
    );
  }

  return (
    <div>
      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, padding: '10px 14px', background: T.mossSoft, border: `1px dashed ${T.moss}`, borderRadius: T.r3 }}>
        <div style={{ fontSize: 13, color: T.mossDark, fontWeight: 700 }}>
          {confirmedAssumptions} of {totalAssumptions} confirmed
        </div>
        <div style={{ flex: 1, height: 6, margin: '0 14px', background: T.card, borderRadius: T.full, overflow: 'hidden', border: `1px solid ${T.rule}` }}>
          <div style={{ width: `${totalAssumptions ? (confirmedAssumptions / totalAssumptions) * 100 : 0}%`, height: '100%', background: T.moss }}/>
        </div>
        <div style={{ fontFamily: T.mono, fontSize: 11, color: T.mossDark, fontWeight: 800 }}>
          {totalAssumptions ? Math.round((confirmedAssumptions / totalAssumptions) * 100) : 0}%
        </div>
      </div>

      {/* Assumptions by category */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {Object.entries(assumptionsByCategory).map(([category, categoryAssumptions]) => {
          const config = ASSUMPTION_CATEGORIES[category] || {
            label: category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' '),
            glyph: '◉',
            color: T.inkMuted,
          };

          return (
            <div key={category}>
              {/* Category header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 6, borderBottom: `1px dashed ${T.rule}` }}>
                <span style={{ fontSize: 14, color: config.color }}>{config.glyph}</span>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.ink2 }}>{config.label}</div>
                <div style={{ flex: 1 }}/>
                <div style={{ fontFamily: T.mono, fontSize: 10, color: T.inkMuted, fontWeight: 700 }}>{categoryAssumptions.length}</div>
              </div>

              {/* 2-column grid of assumption cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {categoryAssumptions.map((a) => {
                  const hasValue = Array.isArray(a.value) ? a.value.length > 0 : (typeof a.value === 'string' ? a.value.trim() : !!a.value);
                  const isEditing = editingQuestion === a.id;

                  return (
                    <div
                      key={a.id}
                      style={{
                        background: hasValue ? T.paperSoft : T.paper,
                        border: `1px solid ${T.rule}`,
                        borderLeft: `3px solid ${hasValue ? T.moss : config.color}`,
                        borderRadius: T.r3,
                        padding: 14,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        {/* Checkmark/question badge */}
                        <div style={{
                          width: 22, height: 22, borderRadius: T.full,
                          background: hasValue ? T.moss : T.paperDeep,
                          color: hasValue ? '#fff' : T.inkMuted,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 800, flexShrink: 0, marginTop: 1,
                          border: hasValue ? `1px solid ${T.mossDark}` : `1px solid ${T.rule}`,
                        }}>{hasValue ? '✓' : '?'}</div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Question text */}
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, lineHeight: 1.45 }}>{a.assumption || a.question}</div>
                          {/* Show current value as source/hint */}
                          {hasValue && a.value && (
                            <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 4, lineHeight: 1.45, fontStyle: 'italic' }}>
                              {typeof a.value === 'string' ? a.value : JSON.stringify(a.value)}
                            </div>
                          )}
                          {/* Edit mode */}
                          {isEditing && (
                            <div style={{ marginTop: 10, padding: '8px 10px', background: T.card, border: `1px dashed ${T.rule}`, borderRadius: T.r2 }}>
                              {a.type === 'choice' && a.options ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: 8 }}>
                                  {a.options.map((option, i) => (
                                    <label
                                      key={i}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '6px 8px',
                                        backgroundColor: T.paperSoft,
                                        borderRadius: T.r2,
                                        cursor: 'pointer',
                                        border: `1px solid ${editValue === option ? T.orange : T.rule}`,
                                      }}
                                    >
                                      <input
                                        type="radio"
                                        name={a.id}
                                        value={option}
                                        checked={editValue === option}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        style={{ accentColor: T.orange }}
                                      />
                                      <span style={{ fontSize: '13px', color: T.ink }}>{option}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <input
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  placeholder="Override this assumption…"
                                  style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13, fontFamily: T.sans, color: T.ink, boxSizing: 'border-box' }}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
                                  autoFocus
                                />
                              )}
                              <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                                <button onClick={handleCancel} style={{ background: 'transparent', border: `1px solid ${T.rule}`, borderRadius: T.r2, color: T.inkMuted, fontSize: 11, fontWeight: 700, padding: '3px 8px', cursor: 'pointer', fontFamily: T.sans }}>Cancel</button>
                                <button onClick={handleSave} disabled={analyzeMutation?.isPending || applyMutation?.isPending} style={{ background: T.orange, border: 'none', borderRadius: T.r2, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', cursor: 'pointer', fontFamily: T.sans }}>Save</button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Edit button */}
                        {!isEditing && (
                          <button
                            onClick={() => handleEdit(a)}
                            style={{ background: 'transparent', border: `1px dashed ${T.rule}`, color: T.inkMuted, borderRadius: T.r2, fontSize: 11, fontWeight: 700, padding: '3px 8px', cursor: 'pointer', fontFamily: T.sans, flexShrink: 0 }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add assumption form / button */}
              {addingToCategory === category ? (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: T.paperSoft,
                  borderRadius: T.r3,
                  border: `1px solid ${T.ruleStrong}`,
                }}>
                  <textarea
                    value={newAssumptionText}
                    onChange={(e) => setNewAssumptionText(e.target.value)}
                    placeholder="Enter your assumption question (e.g., 'What is the expected user base size?')..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: T.card,
                      border: `1px solid ${T.rule}`,
                      borderRadius: T.r2,
                      fontSize: '13px',
                      color: T.ink,
                      resize: 'vertical',
                      fontFamily: T.sans,
                      marginBottom: '8px',
                      minHeight: '60px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      lineHeight: 1.6,
                    }}
                    rows={2}
                    autoFocus
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setAddingToCategory(null);
                        setNewAssumptionText('');
                      }}
                      style={{
                        background: 'transparent',
                        border: `1px dashed ${T.rule}`,
                        borderRadius: T.r2,
                        color: T.inkMuted,
                        cursor: 'pointer',
                        fontSize: '13px',
                        padding: '5px 12px',
                        fontFamily: T.sans,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAddAssumption(category)}
                      disabled={!newAssumptionText.trim() || updateQuestionsMutation.isPending}
                      style={{
                        background: T.orange,
                        border: 'none',
                        borderRadius: T.r3,
                        color: '#fff',
                        cursor: newAssumptionText.trim() ? 'pointer' : 'not-allowed',
                        fontSize: '13px',
                        fontWeight: 600,
                        padding: '5px 14px',
                        fontFamily: T.sans,
                        opacity: (!newAssumptionText.trim() || updateQuestionsMutation.isPending) ? 0.6 : 1,
                      }}
                    >
                      Add Assumption
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAddingToCategory(category)}
                  style={{ marginTop: 8, background: 'transparent', border: `1px dashed ${T.rule}`, borderRadius: T.r3, color: T.inkFaint, fontSize: 12, padding: '8px', cursor: 'pointer', fontFamily: T.sans, width: '100%' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkFaint; }}
                >
                  + Add assumption
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Cascade Modal */}
      <IdeaCascadeModal
        isOpen={cascadeModal.open}
        onClose={() => {
          setCascadeModal({ open: false, data: null, pendingSave: null });
        }}
        directChange={cascadeModal.data?.direct_change}
        cascadeChanges={cascadeModal.data?.cascade_changes || []}
        affectedPlans={cascadeModal.data?.affected_projects || []}
        summary={cascadeModal.data?.summary}
        warnings={cascadeModal.data?.warnings || []}
        onApply={handleCascadeApply}
        isApplying={applyMutation.isPending}
      />
    </div>
  );
};

export default AssumptionsContent;
